import {HostListener, Injectable} from '@angular/core';
import { AuthenticationService } from './authentication.service';
import { CentralAuthenticationService } from './central-authentication.service';
import { HttpClient } from '@angular/common/http';
import { PORT, PREFIX } from '../../environments/environment';
import { CacheService } from 'ionic-cache';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, share, shareReplay, startWith } from 'rxjs/operators';
import { Refresh } from './api.service';
import { UntypedFormBuilder } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  @HostListener('window:resize', ['$event'])

  public $screenWidth = new BehaviorSubject<number>(document.documentElement.clientWidth);

  public readonly breakpointSm = 750;

  public readonly isMobile$: Observable<boolean> = this.$screenWidth.pipe(
    map(w => w < this.breakpointSm),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly isDesktop$: Observable<boolean> = this.$screenWidth.pipe(
    map(w => w >= this.breakpointSm),
    distinctUntilChanged(),
    shareReplay(1)
  );

  private url = 'theme';
  private groupKey = 'style';
  private resizeObservable = fromEvent(window, 'resize').pipe(share(), debounceTime(10));

  private stylesheet = document.querySelector(':root') as HTMLElement;
  private ttl = 60 * 60 * 24;

  constructor(private authenticationService: AuthenticationService,
              private centralAuthService: CentralAuthenticationService,
              private http: HttpClient,
              private cache: CacheService,
              private formBuilder: UntypedFormBuilder) {
    this.centralAuthService.$current.subscribe(tenant => {
      this.loadTheme(tenant, 'all');
    });
    const resizeSubscription$ = this.resizeObservable
      .pipe(
        startWith({ currentTarget: window } as any),
        map((event: any) => {
          const target = event?.currentTarget;
          return target && typeof target.innerWidth === 'number'
            ? target.innerWidth
            : window.innerWidth; // Fallback für Firefox
        }),
        distinctUntilChanged()
      )
      .subscribe(width => {
        this.$screenWidth.next(width);
      });
  }

  public get width(): number {
    return this.$screenWidth.value;
  }

  public getContrastColor = (hexColor) => {
    if (hexColor) {
      const rgb = hexToRgb(hexColor);
      const cont = contrast(rgb, [0, 0, 0]) > contrast(rgb, [255, 255, 255]) ? [0, 0, 0] : [255, 255, 255];
      return rgbToHex(cont);
    } else {
      return rgbToHex([0, 0, 0]);
    }

  };

  private loadTheme(tenant, refresh: Refresh = 'expired') {
    if (tenant) {
      const request = this.http.get<any>(PREFIX + tenant.domains[0].domain + PORT + '/api/' + this.url);
      const response = this.cache.loadFromDelayedObservable(
        tenant.domains[0].domain + '-theme',
        request,
        this.groupKey,
        this.ttl,
        refresh
    );

      response.subscribe(theme => {
        if (Object.keys(theme).length > 0) {
          this.setFromTheme(theme);
        }

      });
    } else {
      const theme = {
        primary: '#573192',
        secondary: '#f48226',
        tertiary: '#ec298a',
        success: '#2dd36f',
        warning: '#ffc409',
        danger: '#eb445a',
        dark: '#231e1e',
        medium: '#454342',
        light: '#9fa0a1',
      };
      this.setFromTheme(theme);
    }

  }

  private setFromTheme(theme) {
    const shadeMultiplier = theme.shade ? theme.shade : 0.9;
    const tintMultiplier = theme.tint ? theme.tint : 0.1;
    for (const key of Object.keys(theme)) {
      if (theme[key] && theme[key].length === 7) {
        if (key.includes('dark_')) {
          // TODO: manage custom dark theme
          console.log(theme[key], key);
        } else {
          const rgb = hexToRgb(theme[key]);
          const cont = contrast(rgb, [0, 0, 0]) > contrast(rgb, [255, 255, 255]) ? [0, 0, 0] : [255, 255, 255];
          const tint = [];
          const shade = [];
          for (const color of rgb) {
            tint.push(Math.round(color + (255 - color) * tintMultiplier));
            shade.push(Math.round(color * shadeMultiplier));
          }
          this.stylesheet.style.setProperty('--ion-color-' + key, theme[key]);
          this.stylesheet.style.setProperty('--ion-color-' + key + '-rgb', rgb[0] + ', ' + rgb[1] + ', ' + rgb[2]);
          this.stylesheet.style.setProperty('--ion-color-' + key + '-contrast', rgbToHex(cont));
          this.stylesheet.style.setProperty('--ion-color-' + key + '-contrast-rgb', cont[0] + ', ' + cont[1] + ', ' + cont[2]);
          this.stylesheet.style.setProperty('--ion-color-' + key + '-shade', rgbToHex(shade));
          this.stylesheet.style.setProperty('--ion-color-' + key + '-tint', rgbToHex(tint));
        }
      }
    }
  }
}

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
};

const componentToHex = (c) => {
  const hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

const rgbToHex = (rgb) => '#' + componentToHex(rgb[0]) + componentToHex(rgb[1]) + componentToHex(rgb[2]);

const contrast = (rgb1, rgb2) => {
  const lum1 = luminance(rgb1[0], rgb1[1], rgb1[2]);
  const lum2 = luminance(rgb2[0], rgb2[1], rgb2[2]);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) /
    (darkest + 0.05);
};

const luminance = (r, g, b) => {
  const a = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.03928 ?
      v / 12.92 :
      Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

// const st = document.querySelector(':root');
// console.log(st);
// const test = getComputedStyle(st);
// console.log(test);
// // @ts-ignore
// st.style.setProperty('--ion-color-primary', 'black');
